<template>
  <div class="p-6">
    <div class="flex flex-col mb-6">
      <label class="font-semibold text-sm text-grey-700 mb-2">
        {{ $t('frontend_groovemail_mail_tag_name') }}
      </label>
      <text-input
        variant="base"
        v-model="model.name"
        :validator="$v.model.name"
        :placeholder="$t('frontend_groovemail_adminsetting_name')"
      />
    </div>
    <div class="flex flex-col mb-6">
      <label class="font-semibold text-sm text-grey-700 mb-2">
        {{ $t('frontend_groovemail_mail_tag_description') }}
      </label>
      <text-input
        variant="base"
        v-model="model.description"
        :validator="$v.model.description"
        :placeholder="$t('frontend_groovemail_adminsetting_description')"
      />
    </div>

    <div class="flex flex-col mb-6">
      <label class="font-semibold text-sm text-grey-700 mb-2">
        {{ $t('frontend_groovemail_mail_tag_category') }}
      </label>
      <treeselect
        class="custom-treeselect category-select-shadow"
        :class="{
          'error': this.$v.model.category.$error,
        }"
        v-model="model.category"
        :multiple="true"
        :clearable="false"
        :clearOnSelect="true"
        :options="categoryFilters"
        :max-height="230"
        :openDirection="'top'"
        :placeholder="$t('frontend_groovemail_tags_category_filter')"
      />
      <p
        v-if="this.$v.model.category.$error"
        class="w-full error text-danger text-sm"
      >
        {{ $t('frontend_groovemail_mail_tag_category_field') }}
      </p>
      <div
        class="text-primary text-sm flex align-center font-semibold cursor-pointer mt-2"
        @click="opencreatecategory"
      >
        <PlusIcon class="h-5 w-5"/>
        <p>{{ $t('frontend_groovemail_mail_tag_create_new_category') }}</p>
      </div>
    </div>

    <!-- Create new category -->
    <div
      class="flex flex-col mb-6"
      v-bind:style="{ display: this.newcategorybox }"
    >
      <div class="font-semibold text-sm text-grey-700 mb-2">
        {{ $t('frontend_groovemail_mail_tag_new_category_name') }}
      </div>
      <div class="flex">
        <div class="flex-1">
          <text-input
            variant="base"
            v-model="model.categoryname"
            :validator="$v.model.categoryname"
            :placeholder="$t('frontend_groovemail_tags_category_name')"
          />
        </div>
        <div class="flex ml-4">
          <gs-button
            variant="outline"
            size="icon"
            :aria-label="$t('frontend_cancel')"
            @click="hidecategory()"
          >
            <MinusIcon class="h-5 w-5"/>
          </gs-button>

          <gs-button
            class="ml-2"
            variant="primary"
            size="icon"
            :aria-label="$t('frontend_save')"
            @click="savecategory()"
          >
            <PlusIcon class="h-5 w-5"/>
          </gs-button>
        </div>
      </div>
    </div>

    <div class="flex justify-end">
      <gs-button
        variant="outline"
        @click="closeTagModal()"
      >{{ $t('frontend_cancel') }}</gs-button>
      <gs-button
        class="ml-2"
        variant="primary"
        @click="type === 'add' ? createMailTag() : editMailtag()"
      >
        {{
          type === 'add'
            ? $t('frontend_groovemail_mail_tag_create_tag')
            : $t('frontend_groovemail_mail_list_update')
        }}
      </gs-button>
    </div>
  </div>
</template>

<script>
import { required, minLength, maxLength } from 'vuelidate/lib/validators';

import Treeselect from '@riophae/vue-treeselect';

import showAlert from '@/components/sweet-alert-popup.js';
import Category from '@/models/Category';
import Mailtag from '@/models/Mailtag';
import themeConfig from '@/theme/themeConfig';

import '@riophae/vue-treeselect/dist/vue-treeselect.css';

// import icons
import PlusIcon from '@/assets/images/icons/new/plus-small_new.svg';
import MinusIcon from '@/assets/images/icons/new/minus_small_new.svg';

export default {
  data() {
    return {
      categories: [],
      formSubmitted: false,
      newcategorybox: 'none',
      model: {
        name: '',
        description: '',
        category: [],
        categoryname: '',
      },
    };
  },

  validations: {
    model: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
      description: {
        required,
        minLength: minLength(3),
      },
      category: {
        required,
      },
      categoryname: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
    },
  },

  props: {
    type: {
      required: true,
    },
    mail_tag_id: {
      required: true,
    },
    mail_tag_data: {
      required: true,
    },
  },

  async created() {
    await this.fetchCategories();
    if (this.mail_tag_data) {
      this.model.name = this.mail_tag_data.name;
      this.model.description = this.mail_tag_data.description;
      this.model.category = this.mail_tag_data.category;
      this.model.categoryname = this.mail_tag_data.categoryname;
    }
  },

  components: {
    Treeselect,
    PlusIcon,
    MinusIcon
  },

  computed: {
    categoryFilters() {
      const categoryFilters = [];
      if (this.categories && this.categories.length) {
        this.categories.forEach((element) => {
          categoryFilters.push({ id: element.id, label: element.name });
        });
      }
      return categoryFilters;
    },
  },

  methods: {
    opencreatecategory() {
      this.newcategorybox = 'block';
    },

    hidecategory() {
      this.newcategorybox = 'none';
    },

    async savecategory() {
      this.$v.model.categoryname.$touch();

      if (!this.$v.model.categoryname.$invalid) {
        this.$emit('showLoader');
        this.category = new Category({});
        this.category.name = this.model.categoryname;
        const catData = await this.category.save()
          .then(async () => {
            if(catData?.id) {
              this.model.category.push(catData?.id);
            }
            await this.fetchCategories();
            this.$emit('fetchCategories');
            this.model.categoryname = '';
            this.newcategorybox = 'none';
            this.$v.model.categoryname.$reset();
            this.$emit('hideLoader');
          })
          .catch(e => {
            // hide the loader and show the error modal
            this.showErrorModal(e.response.data.error);
          });
      }
    },

    // show the error modal with the error message and hide the loader
    showErrorModal(error) {
      // hide the loader
      this.$emit('hideLoader');

      // show the error modal
      showAlert({
        iconType: 'alert-error',
        html : `
          <style>.swal2-confirm { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-sm text-deep-dark">${error}</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    },

    // fetch the categories
    async fetchCategories() {
      // show the loader
      this.$emit('showLoader');

      // fetch the categories
      this.categories = await Category.$get()
        .then((data) => {
          // hide the loader
          this.$emit('hideLoader');

          // return the data
          return data;
        })
        .catch(e => {
          // hide the loader and show the error modal
          this.showErrorModal(e.response.data.error);
        });
    },

    // create a new mail tag
    async createMailTag() {
      this.$v.model.name.$touch();
      this.$v.model.description.$touch();
      this.$v.model.category.$touch();

      // check if the forms is valid
      if (
        !this.$v.model.name.$invalid &&
        !this.$v.model.description.$invalid &&
        this.model.category &&
        this.model.category.length
      ) {
        // show the loader
        this.$emit('showLoader');
        const mailtag = new Mailtag({});
        mailtag.name = this.model.name;
        mailtag.description = this.model.description;

        const choosenCategories = [];
        this.model.category.forEach((ele) => {
          const categoryFound = this.categories.find((data) => data.id === ele);
          choosenCategories.push(categoryFound);
        });
        mailtag.categories = choosenCategories || [];

        const tagCreated = await mailtag.save()
          .then(() => {
            // hide the loader
            this.$emit('hideLoader');

            this.$emit('fetchTags', tagCreated);
            this.$emit('closeCreateTagsModal');
    
            // reset the form
            this.$v.model.name.$reset();
            this.$v.model.description.$reset();
            this.$v.model.category.$reset();
          })
          .catch(e => {
            // hide the loader and show the error modal
            this.showErrorModal(e.response.data.error);
          });
        }
    },

    // close the modal
    closeTagModal() {
      this.$emit('closeCreateTagsModal');
      this.$emit('closeEditTagsModal');
    },

    // edit the mail tag
    async editMailtag() {
      this.$v.model.name.$touch();
      this.$v.model.description.$touch();
      this.$v.model.category.$touch();

      if (
        !this.$v.model.name.$invalid &&
        !this.$v.model.description.$invalid &&
        this.model.category &&
        this.model.category.length
      ) {
        // show the loader
        this.$emit('showLoader');

        const mailtag = await Mailtag.$find(this.mail_tag_id);
        mailtag.name = this.model.name;
        mailtag.description = this.model.description;
        const choosenCategories = [];
        this.model.category.forEach((ele) => {
          const categoryFound = this.categories.find((data) => data.id === ele);
          choosenCategories.push(categoryFound);
        });
        mailtag.categories = choosenCategories || [];

        await mailtag.save()
          .then(() => {
            // hide the loader
            this.$emit('hideLoader');

            this.$emit('fetchTags');
            this.$emit('closeEditTagsModal');

            // reset the form
            this.$v.model.name.$reset();
            this.$v.model.description.$reset();
            this.$v.model.category.$reset();
          })
          .catch(e => {
            // hide the loader and show the error modal
            this.showErrorModal(e.response.data.error);
          });
      }
    },
  },
};
</script>
